/**
 * Design System — Tokens
 *
 * Apple HIG design tokens. Fully self-contained — no imports from
 * the legacy codebase. All values are Tailwind class strings.
 *
 * Reference: Apple Human Interface Guidelines (iOS 17 / macOS Sonoma)
 * Color palette: Apple system colors (Light appearance)
 * Label colors: Semi-transparent rgba on #3C3C43 base (Apple spec)
 * Type scale: SF Pro equivalents at web-appropriate sizes
 * Spacing: 8pt grid system
 */

export const ds = {

  // ── Color System (Apple HIG Light Appearance) ───────────────────

  color: {
    /**
     * Semantic text colors.
     * Apple label colors use rgba transparency on #3C3C43 base
     * so they blend naturally over any background.
     */
    label: {
      primary:     'text-[#1C1C1E]',                          // Apple systemLabel (near-black)
      secondary:   'text-gray-600',                           // Body text — WCAG AA accessible
      tertiary:    'text-gray-400',                           // Form field labels (matches legacy)
      placeholder: 'placeholder:text-gray-400',               // Input placeholder (matches legacy)
      link:        'text-[#007AFF]',                         // System blue
      destructive: 'text-[#FF3B30]',                         // System red
      success:     'text-[#34C759]',                         // System green
      warning:     'text-[#FF9500]',                         // System orange
      inverse:     'text-white',                              // White text on dark backgrounds
      caption:     'text-[#6E6E73]',                         // Apple systemGray2 — subdued caption text
      floated:     'text-gray-400',                          // Floated label (matches legacy)
    },

    /**
     * Semantic text colors — convenience aliases over label colors.
     * Use when "label" semantics feel awkward (e.g., tooltip body, icon tint).
     */
    text: {
      primary:   'text-[#1C1C1E]',   // Apple systemLabel — near-black (= label.primary)
      secondary: 'text-[#8E8E93]',   // Apple systemGray2 — mid-gray icon/hint
      caption:   'text-[#6E6E73]',   // Apple systemGray  — subdued caption (= label.caption)
    },

    /** Semantic background fills */
    fill: {
      primary:       'bg-white',                  // Primary surface
      secondary:     'bg-[#F2F2F7]',             // Apple systemGroupedBackground
      page:          'bg-gray-50',                // Page background (matches existing pages)
      pageMuted:     'bg-[#F8F8FA]',             // Marketing/landing page muted section bg
      grouped:       'bg-white',                  // Card surface in grouped list
      elevated:      'bg-white',                  // Sheets, modals
      hover:         'hover:bg-black/[0.04]',     // Subtle transparent hover
      active:        'active:bg-black/[0.06]',    // Subtle transparent pressed state
      hoverDanger:   'hover:bg-red-500/[0.05]',  // Danger hover
      tertiary:      'bg-[#E5E5EA]',             // Close buttons, subtle fills (systemGray5)
      tertiaryHover: 'hover:bg-[#D1D1D6]',      // Hover for tertiary (systemGray4)
      selected:      'bg-[#007AFF]/[0.06]',      // Selected item highlight
      destructive:   'bg-[#FF3B30]',             // Destructive fills, notification badges
      overlay:       'bg-black/30 backdrop-blur-sm', // Apple sheet scrim (30% + subtle blur)
      glass:         'bg-white/95 backdrop-blur-sm', // Glass effect (tooltips)
      glassNav:      'bg-white/60 backdrop-blur-sm', // Navigation button glass (media viewer arrows)
      glassNavHover: 'hover:bg-white/95',            // Navigation button glass hover
      dark:          'bg-gray-900',                  // Dark background (tooltips default variant)
      skeleton:      'bg-[#E5E5EA]',             // Skeleton bone fill (systemGray5 — visible on white cards)
    },

    /**
     * Borders & separators.
     * Apple separator = rgba(60,60,67,0.29) — semi-transparent.
     */
    separator: {
      default:  'border-[rgba(60,60,67,0.29)]',  // Apple separator
      field:    'border-gray-200',               // Form field border (matches legacy)
      light:    'border-black/[0.04]',             // Barely-visible card outline (Apple)
      card:     'border-indigo-50',                // Modal & card border — crisp subtle blue tint
      divider:  'bg-[#E5E5EA]',                   // 1px content dividers (systemGray5)
    },

    /** Form field interactive states */
    state: {
      focusBorder: 'border-blue-400',              // Focus blue (matches legacy)
      errorBorder: 'border-red-500',              // Error red (matches legacy)
      successBorder: 'border-green-500',          // Success green (selection confirmed)
      focusRing:   'ring-0',                      // No ring (legacy uses ring-0)
      errorRing:   'ring-0',                      // No ring on error
      successFill: 'bg-green-50',                 // Success background tint
    },

    /** Raw hex/rgba values for style= or non-Tailwind contexts */
    raw: {
      labelBase:      '#3C3C43',                  // Base color for label rgba
      labelPrimary:   '#000000',                  // Pure black
      labelSecondary: 'rgba(60,60,67,0.6)',
      separator:      'rgba(60,60,67,0.29)',
      opaqueSeparator:'#C6C6C8',                  // Apple opaqueSeparator
      systemBlue:     '#007AFF',
      systemRed:      '#FF3B30',
      systemGreen:    '#34C759',
      emerald:        '#059669',                  // Tailwind emerald-600 — muted green for status indicators
      emeraldLight:   '#10b981',                  // Tailwind emerald-500 — lighter green for availability badge
      systemOrange:   '#FF9500',
      amber:          '#D97706',                  // Tailwind amber-600 — muted amber for status indicators
      amberLight:     '#FBBF24',                  // Tailwind amber-400 — lighter amber for icons
      systemGray:     '#636366',
      systemGray2:    '#8E8E93',
      systemGray3:    '#C7C7CC',
      systemGray4:    '#D1D1D6',
      systemGray5:    '#E5E5EA',
      systemGray6:    '#F2F2F7',
      nearBlack:      '#1C1C1E',                  // Apple systemLabel (near-black)
      glass:          'rgba(255,255,255,0.78)',    // Frosted glass background
      toastBg:        'rgba(255,255,255,0.97)',    // Toast pill — near-opaque so overlays don't dim it
      systemIndigo:   '#5856D6',                  // Apple system indigo
      systemPurple:   '#AF52DE',                  // Apple system purple
      systemTeal:     '#30B0C7',                  // Apple system teal
      systemCyan:     '#32ADE6',                  // Apple system cyan
      whatsAppGreen:  '#25D366',                  // WhatsApp brand green
      pageGradient:   'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)', // Subtle blue tint → white
    },

    /** Category tint backgrounds (10% opacity — iOS Settings icon circles) */
    categoryTint: {
      blue:    'rgba(0,122,255,0.10)',    // Service providers, tools
      orange:  'rgba(255,149,0,0.10)',    // Utilities, power
      indigo:  'rgba(88,86,214,0.10)',    // Premium tier
      purple:  'rgba(175,82,222,0.10)',   // Contacts, people
      green:   'rgba(52,199,89,0.10)',    // Links, web
      red:     'rgba(255,59,48,0.10)',     // Critical, destructive
      success: 'rgba(52,199,89,0.08)',    // Success indicator bg
    },

    /** Category border colors (15% opacity — paired with categoryTint backgrounds) */
    categoryBorder: {
      blue:   'rgba(0,122,255,0.15)',
      orange: 'rgba(255,149,0,0.15)',
      purple: 'rgba(175,82,222,0.15)',
      green:  'rgba(52,199,89,0.15)',
    },

    /** Category icon colors (paired with categoryTint backgrounds) */
    categoryIcon: {
      blue:   '#007AFF',
      orange: '#FF9500',
      purple: '#AF52DE',
      green:  '#34C759',
    },

    /** Button color palettes (bg, hover, press, disabled, text) */
    button: {
      primary: {
        bg:         '#007AFF',
        hover:      '#006AE6',
        press:      '#005EC4',
        disabledBg: 'rgba(0,122,255,0.4)',
        text:       'text-white',
      },
      secondary: {
        bg:         '#E5E5EA',
        hover:      '#D1D1D6',
        press:      '#C7C7CC',
        disabledBg: 'rgba(229,229,234,0.6)',
        text:       'text-black',
      },
      danger: {
        bg:         '#FF3B30',
        hover:      '#E6352B',
        press:      '#CC2F26',
        disabledBg: 'rgba(255,59,48,0.4)',
        text:       'text-white',
      },
      tonal: {
        bg:         'rgba(0,122,255,0.1)',
        hover:      'rgba(0,122,255,0.15)',
        press:      'rgba(0,122,255,0.2)',
        disabledBg: 'rgba(0,122,255,0.05)',
        text:       'text-[#007AFF]',
      },
      outline: {
        bg:         'transparent',
        hover:      '#007AFF',
        press:      '#005EC4',
        disabledBg: 'transparent',
        text:       'text-[#007AFF]',
        border:     '#007AFF',
        disabledBorder: 'rgba(0,122,255,0.4)',
      },
    },

    /** Chip/badge color palettes — bg matches tonal button (10% opacity) for visual consistency */
    chip: {
      /** Base chip classes (matches tonal pill button typography) */
      base: 'px-2.5 py-0.5 rounded-full text-[13px] font-semibold',
      /** Type badge in list view rows (Address type, ID type, Insurance type, etc.) */
      listBadge: 'px-2.5 py-0.5 rounded-full text-[13px] font-medium',
      /** Category badge in list rows (Service Provider type, Utility type, etc.) */
      categoryBadge: 'inline-block px-2 py-px rounded-full text-[12px]',
      blue: {
        bg:   'rgba(0,122,255,0.1)',
        text: '#007AFF',
      },
      gray: {
        bg:   'rgba(142,142,147,0.15)',
        text: '#8E8E93',
      },
      green: {
        bg:   'rgba(52,199,89,0.1)',
        text: '#34C759',
      },
      orange: {
        bg:   'rgba(255,149,0,0.1)',
        text: '#FF9500',
      },
      /** Amber for warning states (expiring soon, attention needed) */
      amber: {
        bg:   'rgba(217,119,6,0.1)',
        text: '#D97706',
      },
      /** Red for error/critical states */
      red: {
        bg:   'rgba(255,59,48,0.1)',
        text: '#FF3B30',
      },
      /** Purple for premium/pro badges */
      purple: {
        bg:   'rgba(175,82,222,0.1)',
        text: '#AF52DE',
      },
      teal: {
        bg:   'rgba(48,176,199,0.1)',
        text: '#30B0C7',
      },
    },
  },

  // ── Typography ──────────────────────────────────────────────────

  type: {
    /** Apple HIG type scale */
    largeTitle:   'text-[28px] font-bold',          // 34pt Bold — page titles (web-adapted)
    title3:       'text-xl font-normal',           // 20pt Regular (Apple spec)
    cardTitle:    'text-[19px]',                   // 19px — entity card titles
    headline:     'text-[17px] font-semibold',     // 17pt Semibold
    callout:      'text-[16px]',                     // 16pt Regular — Apple HIG callout
    calloutSemibold: 'text-[17px] font-semibold',   // 17pt Semibold
    brand:        'text-[24px] md:text-[32px] font-semibold tracking-tight', // App name / brand
    brandGradient:    'text-[#007AFF]',                   // System blue — hero highlights
    brandGradientSoft:'text-[#007AFF]',                   // System blue — logo wordmark
    bodySmall:    'text-[14px]',                        // 14px — body -1px for compact tables/lists
    bodyCompact:  'text-[14px]',                        // 14px — compact body for dense info (PlaceSummary)
    body:         'text-[15px]',                        // 15px — Apple subheadline
    bodyMedium:   'text-[16px] font-medium',
    bodySemibold: 'text-[15px] font-semibold',
    /** Comfortable variants — bumped sizes for spacious layouts (e.g., public share pages) */
    headlineComfortable:'text-[18px] font-semibold',   // 18px Semibold — headline + 1px
    calloutComfortable: 'text-[17px]',                 // 17px — callout + 1px
    bodyComfortable:    'text-[16px]',                 // 16px — body + 2px
    captionComfortable: 'text-[15px]',                 // 15px — caption / footnote + 2px
    /** CSS class that bumps child <a>/<span> to captionComfortable — defined in globals.css */
    textComfortable:    'text-comfortable',
    /** Apple HIG display scale — native sizes for public/marketing pages (via Tailwind config) */
    pageTitle:      'text-[32px] font-display font-semibold',        // 32px Display Semibold — page hero headings
    ctaTitle:       'text-title-1 font-display font-semibold',      // 28px Display Semibold — CTA section headings
    pageSubtitle:   'text-[18px]',                                    // 18px Regular — page subtitles
    pageBody:       'text-body',                                     // 17px Regular — full-size body text
    pageCaption:    'text-subheadline',                               // 15px Regular — secondary page text
    footnoteLg:     'text-[14px]',                    // 14px Regular — slightly larger footnote
    filterChip:     'text-[13px]',                    // 13px — filter chip labels
    footnote:       'text-[13px]',                    // 13pt Regular
    footnoteMedium: 'text-[13px] font-medium',
    captionMedium:  'text-[14px] font-medium',       // 14px Medium — chip labels, date badges
    subFootnote:    'text-[12px]',                    // 12px — tooltip body, small annotations
    caption1:     'text-[13px]',                    // 13pt Regular — Apple HIG caption
    caption2:     'text-[11px]',                    // 11pt Regular

    /** Font weights */
    weight: {
      regular:  'font-normal',
      medium:   'font-medium',
      semibold: 'font-semibold',
      bold:     'font-bold',
    },
  },

  // ── Font Families ───────────────────────────────────────────────

  font: {
    system: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
  },

  // ── Corner Radii ────────────────────────────────────────────────

  radius: {
    sm:      'rounded-lg',      // 8px  — toggles, small elements
    popover: 'rounded-[10px]',  // 10px — tooltips, popovers
    md:      'rounded-2xl',     // 16px — buttons, inputs
    lg:   'rounded-[20px]', // 20px — cards, list rows (Apple)
    xl:   'rounded-3xl',    // 24px — modals, sheets
    full: 'rounded-full',   // pill / circle
    /** Raw values for inline-style contexts (imperative DOM) */
    raw: {
      popover: '10px',
      pill:    '18px',
    },
  },

  // ── Shadows ─────────────────────────────────────────────────────

  shadow: {
    card:     'shadow-[0_2px_8px_rgba(0,0,0,0.04)]',
    elevated: 'shadow-[0_8px_24px_rgba(0,0,0,0.12)]',
    sheet:    'shadow-[0_24px_80px_rgba(0,0,0,0.12),0_8px_24px_rgba(0,0,0,0.08)]',
    menu:     'shadow-[0_18px_40px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.08)]',
    tooltip:  'shadow-[0_4px_12px_rgba(0,0,0,0.08)]',
    /** Raw values for inline-style contexts (imperative DOM) */
    raw: {
      tooltip: '0 4px 12px rgba(0,0,0,0.08)',
      toast:   '0 12px 30px rgba(0,0,0,0.10), 0 0 0 0.5px rgba(0,0,0,0.05)',
    },
  },

  // ── Motion / Transitions ────────────────────────────────────────

  transition: {
    fast:     'transition-colors duration-[120ms] ease-in-out',
    standard: 'transition-all duration-200 ease-out',
    slow:     'transition-all duration-300 ease-out',
    /** Smooth float for labels (matches legacy FormInput) */
    label:    'transition-all duration-500 ease-out',
    /** Apple spring curve for push navigation */
    pushCurve:    'cubic-bezier(0.32, 0.72, 0, 1)',
    pushDuration: '280ms',
  },

  // ── Layout Patterns ─────────────────────────────────────────────

  layout: {
    /** Section header (structural only — color via ds.color.label.secondary) */
    sectionHeader: 'text-[16px] font-semibold px-1',
    /** Inset grouped card container (white rounded card) */
    insetCard: 'bg-white rounded-2xl border border-gray-100 overflow-hidden',
    /** List row base — 40px min-height, Apple 120ms hover rhythm */
    row: 'group w-full min-h-[40px] px-3 py-1.5 text-left rounded-2xl transition-colors duration-[120ms] ease-in-out flex items-center gap-3',
    /** Row hover state */
    rowHover: 'hover:bg-black/[0.04] active:bg-black/[0.06]',
    /** Gap between form fields */
    formGap: 'space-y-4',
    /** 2-column form grid (year/make, date/amount pairs) */
    formGrid2: 'grid grid-cols-2 gap-3',
    /** 3-column form grid (date/month/year, short fields) */
    formGrid3: 'grid grid-cols-3 gap-3',
    /** Inline form item row — accounts, attachments, tags (rounded pill, subtle bg) */
    formItemRow: 'flex items-center gap-2 py-2 px-3 rounded-2xl bg-gray-50',
    /** Form checkbox row (favorite, private toggles) */
    checkboxRow: 'flex items-center gap-5 pt-2',
    /** Member picker blue border override */
    memberPicker: '[&>button]:!border-blue-400',
    /** Gap between sections in a list */
    sectionGap: 'space-y-5',
    /** Card internal padding — modal & grouped-card containers */
    cardPadding: 'p-5 sm:p-6',
    /** Public page container — centered narrow column */
    publicPage: 'max-w-3xl mx-auto px-8 pt-8',
    /** Page content area padding — main list pages */
    contentPadding: 'px-6 sm:px-8 pb-8',
    /** Page content max-width container */
    contentContainer: 'max-w-7xl mx-auto',
    /** Margin after filter chips */
    filterMargin: 'mb-2',
    /** Banner padding (plan limit, alerts) */
    bannerPadding: 'pt-6 px-6 sm:px-8',
    /** Context menu dimensions */
    contextMenu: {
      width:   'w-[200px]',
      widthPx: 200,
    },
    /** Apple-style thin scrollbar — defined in globals.css */
    scrollbar: 'apple-scrollbar',
    /** Filter chip pill styling */
    filterChip: 'px-3.5 py-1 rounded-full whitespace-nowrap transition-colors',
    /** Scrollable filter chip row with right fade hint */
    filterChipScroll: 'flex items-center gap-2 ml-auto overflow-x-auto scroll-fade-right',
  },

  /** Animation timing values (ms) */
  animation: {
    /** Card enter stagger delay increment */
    cardStagger: 60,
    /** Auto-hide duration for sensitive data reveal (VIN, passwords, etc.) */
    sensitiveRevealMs: 5000,
  },

  // ── Field Styles ────────────────────────────────────────────

  field: {
    /** Trailing format hint — subtle gray text on right side of input (e.g., "MM/DD/YYYY") */
    formatHint: 'absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[12px] text-gray-400',
    formatHintInline: 'pointer-events-none text-[12px] text-gray-400',
    /** Compact inline picker — doc type dropdown in attachment rows */
    inlinePicker: 'w-[80px] flex-shrink-0',
    /** Inline year range input container (e.g., "2024" or "2024-2025") */
    inlineYearPicker: 'w-[100px] flex-shrink-0',
    /** Compact inline input — matches compact Picker trigger size/style (year, short values) */
    inlineInput: 'w-full px-2 py-1.5 bg-white border border-gray-200 rounded-xl text-[12px] font-medium text-gray-900 placeholder:text-gray-400 outline-none transition-colors duration-150 focus:border-blue-400',
  },

  // ── Interaction Styles ──────────────────────────────────────

  interaction: {
    /** Muted gray action icon — darkens on hover (copy, call, send, etc.) */
    actionIcon: 'text-[#8E8E93] hover:text-[#636366] transition-colors duration-150',
    /** Clickable data text — secondary color that darkens on hover (phone, email, address) */
    clickableText: 'text-gray-600 hover:text-gray-800 transition-colors duration-150',
    /** Gap between grouped action icons (copy, sms, call, etc.) */
    actionIconGap: 'gap-3',
    /** Subtle opacity fade on hover — brand links, muted actions */
    fadeHover: 'hover:opacity-70 transition-opacity',
    /** Blue underline link — inline text actions (Add Manually, + Add notes, etc.) */
    linkButton: 'text-[#007AFF] font-medium [text-decoration:none] hover:[text-decoration:underline] hover:decoration-[#007AFF]/60 underline-offset-2 transition-colors duration-150',
    /** Glossy hover card — lift with shadow + frosted glass effect on hover, settle on press */
    hoverCard: 'rounded-[20px] transition-all duration-200 ease-out hover:shadow-[0_4px_24px_rgba(0,0,0,0.10)] active:scale-[0.98]',
  },

  // ── Icon Sizes ──────────────────────────────────────────────────

  icon: {
    xs:     'w-3 h-3',    // 12px
    footer: 'w-3.5 h-3.5', // 14px — card footer icons (lock, heart)
    sm:     'w-4 h-4',    // 16px
    base:   'w-[18px] h-[18px]', // 18px
    action: 'w-[19px] h-[19px]', // 19px — toolbar action icons (heart, etc.)
    md:     'w-5 h-5',    // 20px
    lg:     'w-6 h-6',    // 24px
    xl:     'w-8 h-8',    // 32px
    '2xl':  'w-12 h-12',  // 48px
    hero:   'w-20 h-20',  // 80px — status/error page icon circles
    /** Button container size for icon-only buttons */
    buttonSm: 'w-6 h-6',  // 24px — small icon button touchable area
    buttonMd: 'w-8 h-8',  // 32px — medium icon button touchable area
    /** Leading icon circle container in modal/list rows */
    containerMd: 'w-10 h-10', // 40px
  },

  // ── Z-Index Layers ──────────────────────────────────────────────

  zIndex: {
    sheet:    'z-[10000]',    // Modals, sheets
    dropdown: 'z-[10200]',   // Pickers, dropdown menus
    tooltip:  'z-[10300]',   // Tooltips (always on top)
    toast:    'z-[99999]',   // Toast notifications (absolute top)
    /** Raw numeric values for inline-style contexts */
    raw: {
      dropdownBackdrop: 10199,
      dropdown: 10200,
      tooltip:  10300,
      toast:    99999,
    },
  },

  // ── Tooltip Labels ──────────────────────────────────────────────
  /** Standard tooltip text for common actions — prevents inconsistent hardcoded strings */
  tooltip: {
    call:       'Click to call',
    text:       'Text',
    sendEmail:  'Click to send email',
    openMaps:   'Open in Maps',
    openWebsite:'Open Website',
    copy:       'Copy',
    copied:     'Copied!',
    seeReviews: 'See Reviews',
    favorite:    'Favorite',
    unfavorite:  'Unfavorite',
    more:        'More',
    edit:        'Click to edit',
    zoomIn:     'Zoom in',
    zoomOut:    'Zoom out',
    view:       'View',
  },

  // ── Admin Card ─────────────────────────────────────────────────
  /** Apple-style card for admin/dashboard pages — clean, borderless, shadow-lifted */

  adminCard: {
    /** Card container — white, subtle shadow, no border */
    container:   'bg-white rounded-2xl overflow-hidden',
    shadow:      '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    /** Card section header — clean white, subtle bottom separator */
    header:      'px-6 py-4',
    headerBorder:'rgba(60,60,67,0.12)',
    /** Metric stat card — compact */
    stat:        'bg-white rounded-2xl p-5',
    statShadow:  '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    /** Form input — Apple-style bordered text field for config forms */
    input:       'w-full px-3 py-2 rounded-lg text-[15px] transition-all duration-150 ease-out focus:outline-none focus:ring-2',
    inputBorder: 'rgba(60,60,67,0.18)',
    /** Disabled input variant */
    inputDisabled: 'w-full px-3 py-2 rounded-lg text-[15px] cursor-not-allowed',
    /** Checkbox row — bordered toggle container */
    checkboxRow: 'flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all duration-150 ease-out',
    /** Table header row */
    tableHeader: 'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-all duration-150 ease-out',
    /** Table row hover */
    tableRowHover: 'transition-all duration-150 ease-out',
    /** User avatar — circular initials badge */
    avatar:      'w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 text-sm',
  },

  // ── Segmented Control ──────────────────────────────────────────
  /** Apple iOS-style segmented control tokens */

  segmentedControl: {
    container: 'inline-flex p-[3px] rounded-[10px]',
    containerBg: 'rgba(118,118,128,0.12)',
    tab: {
      base:     'relative px-4 py-[6px] rounded-[7px] text-[13px] font-medium transition-all duration-200 ease-out cursor-pointer select-none flex items-center gap-1.5 whitespace-nowrap',
      active:   '#1C1C1E',
      inactive: '#636366',
      activeBg: '#FFFFFF',
      shadow:   '0 3px 8px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.06)',
    },
    badge: {
      base: 'min-w-[18px] h-[18px] px-1 text-[11px] font-semibold rounded-full inline-flex items-center justify-center text-white',
      bg:   '#FF3B30',
    },
  },

  // ── Sidebar ─────────────────────────────────────────────────────
  /** macOS System Settings-inspired sidebar tokens */

  sidebar: {
    bg:          'bg-[#F7F7F8]',
    borderRight: 'border-r border-black/[0.06]',
    menuButton:  'p-2.5 rounded-[10px] hover:bg-black/[0.05] transition-all duration-200',
    hamburger: {
      container: 'flex flex-col items-center space-y-1.5',
      line:      'h-0.5 bg-gray-600/80 rounded-full backdrop-blur-sm transform transition-all duration-500 group-hover:bg-gray-700 group-hover:scale-110 group-hover:shadow-sm',
      wide:      'w-6',
      medium:    'w-5',
      narrow:    'w-4',
    },
    navIcon: {
      base:     'p-2.5 rounded-xl transition-all duration-200',
      active:   'bg-black/[0.06]',
      inactive: 'group-hover:bg-black/[0.04]',
    },
    navIconColor: {
      active:   'text-[#1C1C1E]',
      inactive: 'text-gray-500 group-hover:text-gray-700',
    },
    navText: {
      base:     'whitespace-nowrap text-[16px] leading-[1.25] transition-colors duration-200',
      active:   'font-semibold text-[#1C1C1E]',
      inactive: 'font-medium text-[#4B5563] group-hover:text-gray-700',
    },
    /** Sidebar role label (Owner, Spouse, Child) */
    roleText:   'text-[12px]',
    /** Sidebar member avatar */
    avatar:     'w-[29px] h-[29px] rounded-full overflow-hidden flex-shrink-0',
    countBadge: {
      bg:    '',
      color: '#FFFFFF',
    },
    activeIndicator:         'absolute left-0 top-1/2 -translate-y-1/2 h-7 w-[2px] rounded-full hidden',
    activeIndicatorGradient: '#1C1C1E',
    iconStrokeWidth:         1.75 as number,
  },

  // ── Landing Page ──────────────────────────────────────────────
  /** Apple.com-scale marketing tokens — large typography, generous spacing, calm cards */

  landing: {
    /** Typography — Apple.com-scale headings for marketing pages */
    type: {
      heroTitle:    'text-[48px] sm:text-[56px] lg:text-[64px] font-display font-semibold tracking-tight leading-[1.05]',
      sectionTitle: 'text-[32px] sm:text-[40px] font-display font-semibold tracking-tight leading-[1.1]',
      sectionBody:  'text-[18px] sm:text-[20px] leading-relaxed',
      cardTitle:    'text-[20px] font-display font-semibold',
      cardBody:     'text-[15px] leading-relaxed',
      navLink:      'text-[14px] font-medium',
      badge:        'text-[13px] font-medium',
      trustBadge:   'text-[14px] font-medium',
      price:        'text-[36px] font-bold tracking-tight',
    },
    /** Generous white-space between sections */
    spacing: {
      sectionY:   'py-16 sm:py-24',
      heroY:      'pt-20 sm:pt-28 pb-16',
      sectionGap: 'mb-12 sm:mb-16',
      cardGap:    'gap-6',
      navH:       'h-14',
    },
    /** Apple.com-width content columns */
    layout: {
      maxW:       'max-w-[980px] mx-auto',
      maxWWide:   'max-w-[1120px] mx-auto',
      maxWNarrow: 'max-w-[680px] mx-auto',
      sectionPx:  'px-6 sm:px-8',
    },
    /** Barely-visible card — white, subtle border, whisper shadow */
    card: {
      base:  'bg-white rounded-[20px] border border-gray-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]',
      hover: 'transition-all duration-200 ease-out hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)]',
      /** Muted gray card — Apple marketing style (#F5F5F7) */
      muted: 'rounded-2xl bg-[#F5F5F7]',
      /** Raw CSS values for inline-style card backgrounds */
      raw: {
        /** Subtle blue wash gradient — pricing highlight card */
        blueTintGradient: 'linear-gradient(180deg, rgba(0,122,255,0.06) 0%, rgba(0,122,255,0.02) 100%)',
      },
    },
  },

} as const;
